/**
 * ArticlesList component for displaying and filtering a list or grid of articles
 * Following the pattern of EventsList with filters
 */

'use client';

import { SearchIcon } from 'lucide-react';
import { ArticleCard } from '../cards/article-card';
import { Pagination } from '../components/ui/pagination';
import { ArticlesFilters } from '../filters/articles-filters';
import { useArticlesFilter } from '../hooks/use-articles-filter';
import type { Article } from '../types';

interface ThemeNode {
  id: string;
  name: string;
  color?: string;
  level?: number;
  children?: ThemeNode[];
  [key: string]: any;
}

interface ArticlesListProps {
  /** Array of articles to display */
  articles: Article[];
  /** Base URL for article links */
  baseUrl?: string;
  /** Layout variant */
  variant?: 'default' | 'compact' | 'featured';
  /** Page size for pagination */
  pageSize?: number;
  /** Show filters sidebar (default: true) */
  showFilters?: boolean;
  /** Whether article cards should be clickable (default: false - shows Read button) */
  clickable?: boolean;
  /** Optional themes tree for hierarchical theme filtering with colors */
  themesTree?: ThemeNode[];
  /** Enable/disable specific filters */
  enabledFilters?: {
    search?: boolean;
    stickyOnly?: boolean;
    themes?: boolean;
    tags?: boolean;
  };
}

/**
 * ArticlesList component displays a responsive grid of articles with filters
 *
 * Features include:
 * - Search filtering by title, subtitle, tags, themes, and content
 * - Theme and tag filters
 * - Sticky articles toggle
 * - Pagination
 * - Accessible with ARIA live regions
 * - Responsive grid layout
 *
 * @example
 * ```tsx
 * // Basic usage with filters
 * <ArticlesList
 *   articles={articles}
 *   baseUrl="/news/articles"
 *   variant="default"
 *   pageSize={12}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // Without filters sidebar
 * <ArticlesList
 *   articles={articles}
 *   showFilters={false}
 *   variant="compact"
 * />
 * ```
 */
export function ArticlesList({
  articles,
  baseUrl = '/news/articles',
  variant = 'default',
  pageSize = 12,
  showFilters = true,
  clickable = false,
  themesTree = [],
  enabledFilters = { search: true, stickyOnly: false, themes: false, tags: true },
}: ArticlesListProps) {
  const {
    query,
    selectedThemes,
    selectedTags,
    themeFilterMode,
    tagFilterMode,
    stickyOnly,
    currentPage,
    themes,
    tags,
    visibleArticles,
    totalResults,
    hasResults,
    hasActiveFilters,
    totalPages,
    startIndex,
    endIndex,
    normalizedQuery,
    setQuery,
    toggleTheme,
    toggleTag,
    clearThemeSelection,
    clearTagSelection,
    setThemeFilterMode,
    setTagFilterMode,
    setStickyOnly,
    handlePageChange,
    clearAllFilters,
  } = useArticlesFilter({ articles, pageSize, themesTree });

  const content = (
    <main className="space-y-6 w-full min-w-0">
      {/* Screen reader announcement for filter changes */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {hasResults
          ? `${totalResults} ${totalResults === 1 ? 'article' : 'articles'} found`
          : hasActiveFilters
          ? 'No articles match your filters'
          : 'No articles available'}
      </div>

      {/* Results Summary */}
      {hasResults && (
        <div className="flex items-center justify-end text-sm text-muted-foreground">
          <p>
            Showing {startIndex + 1}–{endIndex} of {totalResults}{' '}
            {totalResults === 1 ? 'article' : 'articles'}
          </p>
        </div>
      )}

      {/* Articles Grid */}
      {hasResults ? (
        <>
          <ul className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {visibleArticles.map(article => (
              <li key={article.id} className="w-full min-w-0">
                <ArticleCard
                  article={article}
                  baseUrl={baseUrl}
                  variant={variant}
                  clickable={clickable}
                  searchTerm={normalizedQuery}
                />
              </li>
            ))}
          </ul>

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </>
      ) : (
        <div className="flex min-h-[300px] flex-col items-center justify-center rounded-lg border border-dashed text-center">
          <SearchIcon className="mb-4 h-12 w-12 text-muted-foreground/50" />
          <p className="text-lg font-medium text-muted-foreground">
            {hasActiveFilters ? 'No articles match your filters.' : 'No articles available.'}
          </p>
        </div>
      )}
    </main>
  );

  if (!showFilters) {
    return <div className="w-full">{content}</div>;
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px] w-full max-w-full">
      {/* Articles Grid */}
      <div className="lg:col-start-1 w-full min-w-0">{content}</div>

      {/* Filters Sidebar */}
      <aside className="w-full lg:col-start-2 lg:row-start-1 lg:w-80 lg:justify-self-end">
        <ArticlesFilters
          query={query}
          onQueryChange={setQuery}
          themes={themes}
          selectedThemes={selectedThemes}
          onToggleTheme={toggleTheme}
          onClearThemeSelection={clearThemeSelection}
          themeFilterMode={themeFilterMode}
          onThemeFilterModeChange={setThemeFilterMode}
          tags={tags}
          selectedTags={selectedTags}
          onToggleTag={toggleTag}
          onClearTagSelection={clearTagSelection}
          tagFilterMode={tagFilterMode}
          onTagFilterModeChange={setTagFilterMode}
          stickyOnly={stickyOnly}
          onStickyOnlyChange={setStickyOnly}
          totalResults={totalResults}
          hasActiveFilters={hasActiveFilters}
          onClearAllFilters={clearAllFilters}
          enabledFilters={enabledFilters}
        />
      </aside>
    </div>
  );
}

